import { api } from './api';

export type IssueType = 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
export type IssuePriority = 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
export type StatusCategory = 'TODO' | 'IN_PROGRESS' | 'DONE';

export interface ProjectSummary {
  id: string;
  name: string;
  key: string;
  description: string | null;
  memberCount: number;
  openIssueCount: number;
  archivedAt: string | null;
  role: 'LEAD' | 'MEMBER' | 'VIEWER' | null;
  leadUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  leadUser: { id: string; name: string; email: string } | null;
  creator: { id: string; name: string; email: string };
  statusCount: number;
}

export interface ProjectMemberRow {
  userId: string;
  name: string;
  email: string;
  role: 'LEAD' | 'MEMBER' | 'VIEWER';
  joinedAt: string;
}

export interface IssueStatusRow {
  id: string;
  name: string;
  category: StatusCategory;
  order: number;
  colorHex: string;
  isDefault: boolean;
  wipLimit: number | null;
  issueCount: number;
}

export interface IssueSummary {
  id: string;
  key: string;
  title: string;
  type: IssueType;
  priority: IssuePriority;
  statusId: string;
  statusName: string;
  statusCategory: StatusCategory;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  reporterId: string;
  parentIssueId: string | null;
  sprintId: string | null;
  storyPoints: number | null;
  originalEstimateMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueDetail extends IssueSummary {
  descriptionMarkdown: string | null;
  reporter: { id: string; name: string; email: string } | null;
  assignee: { id: string; name: string; email: string } | null;
  parent: { id: string; key: string; title: string; type: IssueType } | null;
  subtasks: IssueSummary[];
  linkedIssues: Array<{
    id: string;
    linkType: 'BLOCKS' | 'IS_BLOCKED_BY' | 'RELATES_TO' | 'DUPLICATES' | 'DUPLICATED_BY';
    direction: 'outgoing' | 'incoming';
    issue: { id: string; key: string; title: string; type: IssueType };
  }>;
  commentsCount: number;
  attachments: Array<{
    id: string;
    originalFilename: string;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
  }>;
  timelogs: Array<{
    id: string;
    userId: string;
    userName: string;
    minutes: number;
    loggedAt: string;
    description: string | null;
  }>;
  activity: Array<{
    id: string;
    action: string;
    metadata: unknown;
    actorId: string | null;
    actorName: string | null;
    createdAt: string;
  }>;
  project: { id: string; key: string; name: string };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export async function listProjects(
  orgId: string,
  query: { search?: string; includeArchived?: boolean; limit?: number; offset?: number } = {},
) {
  const { data } = await api.get<PaginatedResponse<ProjectSummary>>(`/orgs/${orgId}/projects`, {
    params: query,
  });
  return data;
}

export async function createProject(
  orgId: string,
  body: { name: string; key: string; description?: string },
) {
  const { data } = await api.post<ProjectDetail>(`/orgs/${orgId}/projects`, body);
  return data;
}

export async function getProjectByKey(orgId: string, key: string) {
  const { data } = await api.get<ProjectDetail>(
    `/orgs/${orgId}/projects/by-key/${encodeURIComponent(key)}`,
  );
  return data;
}

export async function updateProject(
  orgId: string,
  projectId: string,
  body: {
    name?: string;
    description?: string | null;
    leadUserId?: string | null;
    archived?: boolean;
  },
) {
  const { data } = await api.patch<ProjectDetail>(`/orgs/${orgId}/projects/${projectId}`, body);
  return data;
}

export async function deleteProject(orgId: string, projectId: string) {
  await api.delete(`/orgs/${orgId}/projects/${projectId}`);
}

export async function listProjectMembers(orgId: string, projectId: string) {
  const { data } = await api.get<{ items: ProjectMemberRow[] }>(
    `/orgs/${orgId}/projects/${projectId}/members`,
  );
  return data.items;
}

export async function addProjectMember(
  orgId: string,
  projectId: string,
  body: { userId: string; role: 'LEAD' | 'MEMBER' | 'VIEWER' },
) {
  const { data } = await api.post<ProjectMemberRow>(
    `/orgs/${orgId}/projects/${projectId}/members`,
    body,
  );
  return data;
}

export async function removeProjectMember(orgId: string, projectId: string, userId: string) {
  await api.delete(`/orgs/${orgId}/projects/${projectId}/members/${userId}`);
}

export async function listStatuses(orgId: string, projectId: string) {
  const { data } = await api.get<{ items: IssueStatusRow[] }>(
    `/orgs/${orgId}/projects/${projectId}/statuses`,
  );
  return data.items;
}

export async function createStatus(
  orgId: string,
  projectId: string,
  body: {
    name: string;
    category: StatusCategory;
    colorHex?: string;
    wipLimit?: number | null;
    order?: number;
  },
) {
  const { data } = await api.post<IssueStatusRow>(
    `/orgs/${orgId}/projects/${projectId}/statuses`,
    body,
  );
  return data;
}

export async function updateStatus(
  orgId: string,
  projectId: string,
  statusId: string,
  body: {
    name?: string;
    category?: StatusCategory;
    colorHex?: string;
    wipLimit?: number | null;
    order?: number;
  },
) {
  const { data } = await api.patch<IssueStatusRow>(
    `/orgs/${orgId}/projects/${projectId}/statuses/${statusId}`,
    body,
  );
  return data;
}

export async function reorderStatuses(orgId: string, projectId: string, orderedIds: string[]) {
  const { data } = await api.put<{ items: IssueStatusRow[] }>(
    `/orgs/${orgId}/projects/${projectId}/statuses/reorder`,
    { orderedIds },
  );
  return data.items;
}

export async function deleteStatus(
  orgId: string,
  projectId: string,
  statusId: string,
  replacementStatusId: string,
) {
  await api.delete(`/orgs/${orgId}/projects/${projectId}/statuses/${statusId}`, {
    data: { replacementStatusId },
  });
}

export async function listIssues(
  orgId: string,
  projectId: string,
  query: {
    type?: IssueType[];
    statusId?: string[];
    assigneeId?: string[];
    sprintId?: string | 'none';
    searchText?: string;
    hasParent?: boolean;
    limit?: number;
    offset?: number;
    sort?: 'createdAt' | 'updatedAt' | 'priority' | 'storyPoints';
    order?: 'asc' | 'desc';
  } = {},
) {
  const params: Record<string, string | number | boolean> = {};
  if (query.type?.length) params.type = query.type.join(',');
  if (query.statusId?.length) params.statusId = query.statusId.join(',');
  if (query.assigneeId?.length) params.assigneeId = query.assigneeId.join(',');
  if (query.sprintId !== undefined) params.sprintId = query.sprintId;
  if (query.searchText) params.searchText = query.searchText;
  if (query.hasParent !== undefined) params.hasParent = query.hasParent;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset !== undefined) params.offset = query.offset;
  if (query.sort) params.sort = query.sort;
  if (query.order) params.order = query.order;

  const { data } = await api.get<PaginatedResponse<IssueSummary>>(
    `/orgs/${orgId}/projects/${projectId}/issues`,
    { params },
  );
  return data;
}

export async function createIssue(
  orgId: string,
  projectId: string,
  body: {
    type: IssueType;
    title: string;
    descriptionMarkdown?: string;
    assigneeId?: string | null;
    parentIssueId?: string | null;
    priority?: IssuePriority;
    storyPoints?: number | null;
    sprintId?: string | null;
    statusId?: string;
  },
) {
  const { data } = await api.post<IssueDetail>(`/orgs/${orgId}/projects/${projectId}/issues`, body);
  return data;
}

export async function getIssue(orgId: string, idOrKey: string) {
  const { data } = await api.get<IssueDetail>(`/orgs/${orgId}/issues/${idOrKey}`);
  return data;
}

export async function updateIssue(
  orgId: string,
  idOrKey: string,
  body: {
    title?: string;
    descriptionMarkdown?: string | null;
    statusId?: string;
    assigneeId?: string | null;
    priority?: IssuePriority;
    storyPoints?: number | null;
    sprintId?: string | null;
    parentIssueId?: string | null;
  },
) {
  const { data } = await api.patch<IssueDetail>(`/orgs/${orgId}/issues/${idOrKey}`, body);
  return data;
}

export async function deleteIssue(orgId: string, idOrKey: string) {
  await api.delete(`/orgs/${orgId}/issues/${idOrKey}`);
}
